import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import api from '../services/api';
import BankLinkButton from '../components/bank/BankLinkButton';
import BankAccounts from '../components/bank/BankAccounts';
import {
  Wallet,
  Building2,
  Globe,
  Copy,
  RefreshCw,
  Plus,
  CheckCircle2,
  AlertCircle,
  Landmark,
  ArrowDownUp,
  ExternalLink,
  Shield,
  Star,
  Phone,
  Mail,
  MapPin,
  Info,
  BarChart3,
  FileText,
  TrendingUp,
  DollarSign,
  Calendar,
  CreditCard,
  Lock,
  Eye,
  EyeOff,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { TENANT } from '../config';
import ProgramManagerDashboard from '../components/Admin/ProgramManagerDashboard';
import SmartContractsTab from './admin/SmartContractsTab';
import ShareConversionAdminTab from './admin/ShareConversionAdminTab';
import LiquidityDistributionsAdminTab from './admin/LiquidityDistributionsAdminTab';

/* ───────────────────────── types ───────────────────────── */

interface IncreaseCard {
  id: string;
  tenant_id: string;
  increase_id: string;
  account_id: string;
  bank_account_id: string | null;
  bank_account_name: string | null;
  description: string;
  last4: string | null;
  expiration_month: number | null;
  expiration_year: number | null;
  status: 'active' | 'disabled';
  billing_address: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
  };
  digital_wallet: {
    email?: string;
    phone?: string;
  };
  created_at: string;
  updated_at: string;
}

interface IncreaseAccount {
  id: string;
  tenant_id: string;
  increase_id: string;
  name: string;
  account_number: string | null;
  routing_number: string | null;
  balance_cents: number;
  currency: string;
  status: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface PlatformWallet {
  id: string;
  address: string;
  purpose: string;
  network: string;
  created_at?: string;
}

interface WireInfo {
  entityName?: string;
  entityAddress?: string;
  phone?: string;
  email?: string;
  bankName?: string;
  bankAddress?: string;
  routingNumber?: string;
  accountName?: string;
  accountType?: string;
  accountNumber?: string;
  swiftCode?: string;
  domesticWireRouting?: string;
  internationalWireSwift?: string;
  directDepositRouting?: string;
  memoTemplate?: string;
  memoExample?: string;
  confirmContact?: string;
}

interface FinancialSummary {
  transaction_count: number;
  settlement_volume_cents: number;
  total_processing_cost_cents: number;
  total_platform_fee_cents: number;
  total_infra_fee_cents: number;
  total_float_cents: number;
  net_revenue_cents: number;
  avg_settlement_days: number;
  monthly_saas_fee_cents: number;
}

interface RailBreakdown {
  rail_type: string;
  txn_count: number;
  volume_cents: number;
  cost_cents: number;
  net_cents: number;
}

interface TenantStatement {
  id: string;
  tenant_id: string;
  period_start: string;
  period_end: string;
  total_transactions: number;
  settlement_volume_cents: number;
  total_processing_cost_cents: number;
  total_platform_fee_cents: number;
  total_infra_fee_cents: number;
  total_float_revenue_cents: number;
  monthly_saas_fee_cents: number;
  net_revenue_cents: number;
  ach_count: number;
  wire_count: number;
  card_count: number;
  crypto_count: number;
  status: string;
  generated_at: string;
}

interface TenantPricing {
  monthly_fee_cents: number;
  ach_cost_cents: number;
  wire_cost_cents: number;
  card_cost_bps: number;
  card_fixed_cents: number;
  platform_fee_bps: number;
  platform_fee_fixed_cents: number;
  investor_infra_fee_bps: number;
  settlement_days_ach: number;
  settlement_days_wire: number;
  fed_funds_rate_bps: number;
}

/* ───────────────────────── component ───────────────────────── */

const AdminTreasury: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'accounts' | 'cards' | 'wallets' | 'banking' | 'wire' | 'infra' | 'clearing' | 'contracts' | 'conversions' | 'liquidity'>('accounts');
  const [accounts, setAccounts] = useState<IncreaseAccount[]>([]);
  const [cards, setCards] = useState<IncreaseCard[]>([]);
  const [syncingCards, setSyncingCards] = useState(false);
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [newCardAccountId, setNewCardAccountId] = useState('');
  const [newCardDescription, setNewCardDescription] = useState('');
  const [newCardLine1, setNewCardLine1] = useState('');
  const [newCardCity, setNewCardCity] = useState('');
  const [newCardState, setNewCardState] = useState('');
  const [newCardZip, setNewCardZip] = useState('');
  const [creatingCard, setCreatingCard] = useState(false);

  // Card details reveal (Increase-hosted PCI-safe iframe)
  const [revealedCardId, setRevealedCardId] = useState<string | null>(null);
  const [revealIframeUrl, setRevealIframeUrl] = useState<string | null>(null);
  const [revealLoading, setRevealLoading] = useState(false);

  // Physical card order modal
  const [physicalCardId, setPhysicalCardId] = useState<string | null>(null);
  const [physFirstName, setPhysFirstName] = useState('');
  const [physLastName, setPhysLastName] = useState('');
  const [physRecipient, setPhysRecipient] = useState('');
  const [physLine1, setPhysLine1] = useState('');
  const [physLine2, setPhysLine2] = useState('');
  const [physCity, setPhysCity] = useState('');
  const [physState, setPhysState] = useState('');
  const [physZip, setPhysZip] = useState('');
  const [physMethod, setPhysMethod] = useState<'usps' | 'fedex_priority_overnight' | 'fedex_2_day'>('usps');
  const [physLoading, setPhysLoading] = useState(false);
  const [platformWallets, setPlatformWallets] = useState<PlatformWallet[]>([]);
  const [wireInfo, setWireInfo] = useState<WireInfo>({});
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [bankRefreshKey, setBankRefreshKey] = useState(0);

  // Create account form
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountDefault, setNewAccountDefault] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);

  // Create wallet form
  const [showCreateWallet, setShowCreateWallet] = useState(false);
  const [newWalletPurpose, setNewWalletPurpose] = useState('treasury');
  const [newWalletNetwork, setNewWalletNetwork] = useState('base');
  const [creatingWallet, setCreatingWallet] = useState(false);

  // Infrastructure / Financials
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary | null>(null);
  const [railBreakdown, setRailBreakdown] = useState<RailBreakdown[]>([]);
  const [statements, setStatements] = useState<TenantStatement[]>([]);
  const [pricing, setPricing] = useState<TenantPricing | null>(null);
  const [generatingStatement, setGeneratingStatement] = useState(false);

  /* ───────────── data loading ───────────── */

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [acctRes, walletRes, wireRes] = await Promise.all([
        api.get('/admin/treasury/accounts').catch(() => null) as Promise<any>,
        api.get('/crypto/admin/wallets').catch(() => null) as Promise<any>,
        api.get('/admin/treasury/wire-info').catch(() => null) as Promise<any>,
      ]);

      if (acctRes?.data) setAccounts(Array.isArray(acctRes.data) ? acctRes.data : []);
      if (walletRes?.data) setPlatformWallets(Array.isArray(walletRes.data) ? walletRes.data : []);

      // Load cards (non-blocking)
      api.get('/admin/treasury/cards').then((cardRes: any) => {
        if (cardRes?.data) setCards(Array.isArray(cardRes.data) ? cardRes.data : []);
      }).catch(() => {});
      if (wireRes?.data) setWireInfo(wireRes.data);

      // Load financials (non-blocking)
      Promise.all([
        api.get('/admin/financials/summary').catch(() => null) as Promise<any>,
        api.get('/admin/financials/by-rail').catch(() => null) as Promise<any>,
        api.get('/admin/financials/statements').catch(() => null) as Promise<any>,
        api.get('/admin/financials/pricing').catch(() => null) as Promise<any>,
      ]).then(([sumRes, railRes, stmtRes, priceRes]) => {
        if (sumRes?.data) setFinancialSummary(sumRes.data);
        if (railRes?.data) setRailBreakdown(Array.isArray(railRes.data) ? railRes.data : []);
        if (stmtRes?.data) setStatements(Array.isArray(stmtRes.data) ? stmtRes.data : []);
        if (priceRes?.data) setPricing(priceRes.data);
      });
    } catch (err) {
      console.error('Treasury load error', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
    toast.success('Treasury data refreshed');
  };

  /* ───────────── sync from Increase API ───────────── */

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.get('/admin/treasury/accounts/sync') as any;
      if (res?.data) setAccounts(Array.isArray(res.data) ? res.data : []);
      toast.success(`Synced ${res?.synced || 0} account(s) from Increase`);
    } catch (err: any) {
      toast.error(err?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  /* ───────────── create Increase account ───────────── */

  const handleCreateAccount = async () => {
    if (!newAccountName.trim()) { toast.error('Account name is required'); return; }
    setCreatingAccount(true);
    try {
      await api.post('/admin/treasury/accounts', {
        name: newAccountName.trim(),
        is_default: newAccountDefault,
      });
      toast.success('Increase account created');
      setShowCreateAccount(false);
      setNewAccountName('');
      setNewAccountDefault(false);
      await handleSync();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create account');
    } finally {
      setCreatingAccount(false);
    }
  };

  /* ───────────── set default account ───────────── */

  const handleSetDefault = async (acct: IncreaseAccount) => {
    try {
      await api.patch(`/admin/treasury/accounts/${acct.id}`, { is_default: true });
      toast.success(`${acct.name} set as default`);
      await loadAll();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update');
    }
  };

  /* ───────────── wallet creation ───────────── */

  const handleCreateWallet = async () => {
    setCreatingWallet(true);
    try {
      await api.post('/crypto/admin/wallets', {
        purpose: newWalletPurpose,
        network: newWalletNetwork,
      });
      toast.success('Platform wallet created');
      setShowCreateWallet(false);
      await loadAll();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create wallet');
    } finally {
      setCreatingWallet(false);
    }
  };

  /* ───────────── helpers ───────────── */

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const fmtUSD = (cents: number | string) => {
    const c = typeof cents === 'string' ? parseInt(cents, 10) : cents;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((c || 0) / 100);
  };

  const shortAddr = (addr: string) =>
    addr.length > 14 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;

  const totalBalance = accounts.reduce((sum, a) => {
    const b = typeof a.balance_cents === 'string' ? parseInt(a.balance_cents as any, 10) : (a.balance_cents || 0);
    return sum + b;
  }, 0);

  /* ───────────── tabs ───────────── */

  const handleSyncCards = async () => {
    setSyncingCards(true);
    try {
      const res = await api.get('/admin/treasury/cards/sync') as any;
      if (res?.data) setCards(Array.isArray(res.data) ? res.data : []);
      toast.success(`Synced ${res?.synced ?? 0} card(s) from Increase`);
    } catch (err: any) {
      toast.error(err?.message || 'Sync failed');
    } finally {
      setSyncingCards(false);
    }
  };

  const handleCreateCard = async () => {
    if (!newCardAccountId) { toast.error('Select a bank account'); return; }
    if (!newCardDescription.trim()) { toast.error('Card description is required'); return; }
    setCreatingCard(true);
    try {
      const payload: any = {
        bank_account_id: newCardAccountId,
        description: newCardDescription.trim(),
      };
      if (newCardLine1.trim()) {
        payload.billing_address = {
          line1: newCardLine1.trim(),
          city: newCardCity.trim(),
          state: newCardState.trim(),
          postal_code: newCardZip.trim(),
        };
      }
      const res = await api.post('/admin/treasury/cards', payload) as any;
      setCards(prev => [res.data, ...prev]);
      toast.success('Card created');
      setShowCreateCard(false);
      setNewCardAccountId('');
      setNewCardDescription('');
      setNewCardLine1('');
      setNewCardCity('');
      setNewCardState('');
      setNewCardZip('');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create card');
    } finally {
      setCreatingCard(false);
    }
  };

  const handleToggleCardStatus = async (card: IncreaseCard) => {
    const newStatus = card.status === 'active' ? 'disabled' : 'active';
    try {
      const res = await api.patch(`/admin/treasury/cards/${card.id}`, { status: newStatus }) as any;
      setCards(prev => prev.map(c => c.id === card.id ? { ...c, ...res.data } : c));
      toast.success(`Card ${newStatus === 'active' ? 'activated' : 'disabled'}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update card');
    }
  };

  const handleRevealCard = async (card: IncreaseCard) => {
    // Toggle off if already revealed for this card
    if (revealedCardId === card.id) {
      setRevealedCardId(null);
      setRevealIframeUrl(null);
      return;
    }
    setRevealedCardId(card.id);
    setRevealIframeUrl(null);
    setRevealLoading(true);
    try {
      const res = await api.post(`/admin/treasury/cards/${card.id}/details-iframe`, {}) as any;
      setRevealIframeUrl(res?.data?.iframe_url || null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to retrieve card details');
      setRevealedCardId(null);
    } finally {
      setRevealLoading(false);
    }
  };

  const handleOpenPhysicalOrder = (card: IncreaseCard) => {
    setPhysicalCardId(card.id);
    // Pre-fill billing address from card if available
    setPhysLine1(card.billing_address?.line1 || '');
    setPhysCity(card.billing_address?.city || '');
    setPhysState(card.billing_address?.state || '');
    setPhysZip(card.billing_address?.postal_code || '');
    setPhysFirstName('');
    setPhysLastName('');
    setPhysRecipient('');
    setPhysLine2('');
    setPhysMethod('usps');
  };

  const handleSubmitPhysicalCard = async () => {
    if (!physFirstName.trim() || !physLastName.trim()) { toast.error('Cardholder name is required'); return; }
    if (!physRecipient.trim() || !physLine1.trim() || !physCity.trim() || !physState.trim() || !physZip.trim()) {
      toast.error('Recipient name and full shipping address are required');
      return;
    }
    setPhysLoading(true);
    try {
      const res = await api.post(`/admin/treasury/cards/${physicalCardId}/physical`, {
        cardholder: { firstName: physFirstName.trim(), lastName: physLastName.trim() },
        shipment: {
          method: physMethod,
          address: {
            name:        physRecipient.trim(),
            line1:       physLine1.trim(),
            line2:       physLine2.trim() || null,
            city:        physCity.trim(),
            state:       physState.trim().toUpperCase().slice(0, 2),
            postal_code: physZip.trim(),
            country:     'US',
          },
        },
      }) as any;
      toast.success(`Physical card ordered — ID: ${res?.data?.id || 'confirmed'}`);
      setPhysicalCardId(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to place physical card order');
    } finally {
      setPhysLoading(false);
    }
  };

  const tabs = [
    { key: 'accounts' as const, label: 'Increase Accounts', icon: <Landmark className="w-4 h-4" /> },
    { key: 'cards' as const, label: 'Debit Cards', icon: <CreditCard className="w-4 h-4" /> },
    { key: 'wallets' as const, label: 'Treasury Wallets', icon: <Wallet className="w-4 h-4" /> },
    { key: 'banking' as const, label: 'Bank Linking', icon: <Building2 className="w-4 h-4" /> },
    { key: 'wire' as const, label: 'Wire / ACH Info', icon: <ArrowDownUp className="w-4 h-4" /> },
    { key: 'contracts' as const, label: 'Smart Contracts', icon: <FileText className="w-4 h-4" /> },
    { key: 'conversions' as const, label: 'Share Conversion', icon: <ArrowDownUp className="w-4 h-4" /> },
    { key: 'liquidity' as const, label: 'Liquidity & Distributions', icon: <DollarSign className="w-4 h-4" /> },
    { key: 'infra' as const, label: 'Infrastructure', icon: <BarChart3 className="w-4 h-4" /> },
    { key: 'clearing' as const, label: TENANT.id === 'redex' ? 'Program Manager' : 'FBO Clearing', icon: <Shield className="w-4 h-4" /> },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    );
  }

  /* ═══════════════════════════════════ RENDER ═══════════════════════════════════ */

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-primary shrink-0" />
            Treasury Management
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Manage platform bank accounts, treasury wallets, and wire information
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-card border border-border rounded-xl p-4 sm:p-5 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Total Balance</span>
            <Landmark className="w-5 h-5 text-primary" />
          </div>
          <p className="text-xl sm:text-2xl font-bold mt-2">{fmtUSD(totalBalance)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Across {accounts.length} Increase account{accounts.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 sm:p-5 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Increase Status</span>
            {accounts.length > 0 ? (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-500" />
            )}
          </div>
          <p className="text-xl sm:text-2xl font-bold mt-2">
            {accounts.length > 0 ? 'Connected' : 'No Accounts'}
          </p>
          <p className="text-xs text-muted-foreground mt-1 truncate">
            {accounts.find(a => a.is_default) ? `Default: ${accounts.find(a => a.is_default)?.name}` : 'No default set'}
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 sm:p-5 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Treasury Wallets</span>
            <Globe className="w-5 h-5 text-primary" />
          </div>
          <p className="text-xl sm:text-2xl font-bold mt-2">{platformWallets.length}</p>
          <p className="text-xs text-muted-foreground mt-1">
            On-chain addresses
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border -mx-3 px-3 sm:mx-0 sm:px-0">
        <nav className="flex gap-2 sm:gap-5 overflow-x-auto overscroll-x-contain pb-px" aria-label="Tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`shrink-0 flex items-center gap-2 px-1 py-2 sm:pb-3 text-xs sm:text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                activeTab === t.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ──────── TAB: Increase Accounts ──────── */}
      {activeTab === 'accounts' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-lg font-semibold">Increase Bank Accounts</h2>
            <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
              <button
                onClick={handleSync}
                disabled={syncing}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-border hover:bg-muted disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                Sync Balances
              </button>
              <button
                onClick={() => setShowCreateAccount(!showCreateAccount)}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Plus className="w-4 h-4" /> New Account
              </button>
            </div>
          </div>

          {/* Create Account Form */}
          {showCreateAccount && (
            <div className="bg-card border border-border rounded-xl p-4 sm:p-5 space-y-4">
              <h3 className="font-semibold">Create Increase Bank Account</h3>
              <p className="text-sm text-muted-foreground">
                Creates a new bank account on the Increase platform for {TENANT.branding.name}. You can then attach it to offerings, properties, or engagements.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Account Name</label>
                  <input
                    value={newAccountName}
                    onChange={(e) => setNewAccountName(e.target.value)}
                    placeholder="e.g., Hive Fund Operating"
                    className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background"
                  />
                </div>
                <div className="flex items-center gap-2 mt-6">
                  <input
                    id="acct-default"
                    type="checkbox"
                    checked={newAccountDefault}
                    onChange={(e) => setNewAccountDefault(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <label htmlFor="acct-default" className="text-sm">Set as default account</label>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleCreateAccount}
                  disabled={creatingAccount}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {creatingAccount ? 'Creating…' : 'Create Account'}
                </button>
                <button
                  onClick={() => setShowCreateAccount(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {accounts.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <Landmark className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No Increase accounts found.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Click "Sync Balances" to pull from Increase or "New Account" to create one.
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {accounts.map((acct) => (
                <div
                  key={acct.id}
                  className={`bg-card border rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                    acct.is_default ? 'border-primary/50 ring-1 ring-primary/20' : 'border-border'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Landmark className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="font-semibold truncate">{acct.name || acct.increase_id}</span>
                      {acct.is_default && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          <Star className="w-3 h-3" /> Default
                        </span>
                      )}
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          acct.status === 'open'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {acct.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                      <span className="break-all">Increase ID: {acct.increase_id}</span>
                      <span>Currency: {(acct.currency || 'USD').toUpperCase()}</span>
                    </div>
                  </div>
                  <div className="flex w-full sm:w-auto items-center justify-between sm:justify-end gap-4">
                    <div className="text-left sm:text-right">
                      <p className="text-xl font-bold">{fmtUSD(acct.balance_cents)}</p>
                      <p className="text-xs text-muted-foreground">Balance</p>
                    </div>
                    {!acct.is_default && (
                      <button
                        onClick={() => handleSetDefault(acct)}
                        className="text-xs px-2 py-1 rounded border border-border hover:bg-muted whitespace-nowrap"
                        title="Set as default"
                      >
                        Set Default
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ──────── TAB: Debit Cards ──────── */}
      {activeTab === 'cards' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-lg font-semibold">Debit Cards</h2>
            <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
              <button
                onClick={handleSyncCards}
                disabled={syncingCards}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-border hover:bg-muted disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${syncingCards ? 'animate-spin' : ''}`} />
                Sync Cards
              </button>
              <button
                onClick={() => setShowCreateCard(!showCreateCard)}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Plus className="w-4 h-4" /> New Card
              </button>
            </div>
          </div>

          {/* Create Card Form */}
          {showCreateCard && (
            <div className="bg-card border border-border rounded-xl p-4 sm:p-5 space-y-4">
              <h3 className="font-semibold">Create Virtual Debit Card</h3>
              <p className="text-sm text-muted-foreground">
                Creates a new virtual debit card on Increase linked to the selected bank account.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Bank Account</label>
                  <select
                    value={newCardAccountId}
                    onChange={(e) => setNewCardAccountId(e.target.value)}
                    className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background"
                  >
                    <option value="">Select account…</option>
                    {accounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name} {a.is_default ? '(default)' : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Card Description</label>
                  <input
                    value={newCardDescription}
                    onChange={(e) => setNewCardDescription(e.target.value)}
                    placeholder="e.g., Ops Card"
                    className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Billing Address Line 1</label>
                  <input
                    value={newCardLine1}
                    onChange={(e) => setNewCardLine1(e.target.value)}
                    placeholder="e.g., 7330 Rome Ln"
                    className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="block text-sm font-medium mb-1">City</label>
                    <input
                      value={newCardCity}
                      onChange={(e) => setNewCardCity(e.target.value)}
                      placeholder="Brooksville"
                      className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">State</label>
                    <input
                      value={newCardState}
                      onChange={(e) => setNewCardState(e.target.value.toUpperCase().slice(0,2))}
                      placeholder="FL"
                      maxLength={2}
                      className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background uppercase"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">ZIP</label>
                    <input
                      value={newCardZip}
                      onChange={(e) => setNewCardZip(e.target.value)}
                      placeholder="34613"
                      maxLength={5}
                      className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleCreateCard}
                  disabled={creatingCard}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {creatingCard ? 'Creating…' : 'Create Card'}
                </button>
                <button
                  onClick={() => setShowCreateCard(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {cards.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <CreditCard className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No debit cards found.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Click "Sync Cards" to pull from Increase or "New Card" to create one.
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {cards.map((card) => (
                <div key={card.id} className={`bg-card border rounded-xl overflow-hidden ${card.status === 'active' ? 'border-border' : 'border-yellow-500/40 opacity-75'}`}>
                  {/* ── Card Header Row ── */}
                  <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                      <div className="flex-shrink-0 w-12 h-8 rounded-md bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-primary-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold truncate">{card.description}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${card.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {card.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 mt-1 text-xs text-muted-foreground">
                          <span className="font-mono tracking-widest">•••• •••• •••• {card.last4 || '????'}</span>
                          <span>Exp {card.expiration_month?.toString().padStart(2,'0')}/{card.expiration_year}</span>
                          {card.bank_account_name && <span className="truncate max-w-full">{card.bank_account_name}</span>}
                        </div>
                        {card.billing_address?.line1 && (
                          <div className="flex items-start gap-1 mt-1 text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                            {card.billing_address.line1}, {card.billing_address.city}, {card.billing_address.state} {card.billing_address.postal_code}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* ── Action Buttons ── */}
                    <div className="grid grid-cols-3 sm:flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                      {/* Reveal card details */}
                      <button
                        onClick={() => handleRevealCard(card)}
                        className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          revealedCardId === card.id
                            ? 'border-primary/50 bg-primary/10 text-primary'
                            : 'border-border hover:bg-muted text-foreground'
                        }`}
                        title="Show full card number, CVV, and expiry"
                      >
                        {revealedCardId === card.id
                          ? <><EyeOff className="w-3.5 h-3.5" /> Hide</>
                          : <><Eye className="w-3.5 h-3.5" /> Reveal</>}
                      </button>
                      {/* Order physical card */}
                      <button
                        onClick={() => handleOpenPhysicalOrder(card)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-border hover:bg-muted transition-colors"
                        title="Order a physical card"
                      >
                        <CreditCard className="w-3.5 h-3.5" /> Physical
                      </button>
                      {/* Enable / Disable */}
                      <button
                        onClick={() => handleToggleCardStatus(card)}
                        className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          card.status === 'active'
                            ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'
                            : 'border-green-300 text-green-700 hover:bg-green-50'
                        }`}
                        title={card.status === 'active' ? 'Disable card' : 'Activate card'}
                      >
                        {card.status === 'active'
                          ? <><ToggleRight className="w-4 h-4" /> Disable</>
                          : <><ToggleLeft className="w-4 h-4" /> Enable</>}
                      </button>
                    </div>
                  </div>

                  {/* ── Card Reveal Panel ── */}
                  {revealedCardId === card.id && (
                    <div className="border-t border-border bg-muted/30 p-4 space-y-3">
                      {revealLoading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Loading secure card details…
                        </div>
                      ) : revealIframeUrl ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Lock className="w-3 h-3" />
                            Card details rendered by Increase (PCI-DSS compliant). Numbers are never transmitted to this server.
                          </div>
                          <iframe
                            src={revealIframeUrl}
                            title="Card Details"
                            className="w-full rounded-lg border border-border"
                            style={{ height: 220, minWidth: 0 }}
                            sandbox="allow-scripts allow-same-origin"
                          />
                          <p className="text-xs text-muted-foreground">
                            Copy the card number, CVV, and expiry to add to Apple Pay, Google Pay, or any digital wallet.
                          </p>
                          {(card.digital_wallet?.email || card.digital_wallet?.phone) && (
                            <div className="flex flex-wrap gap-3 text-xs mt-1">
                              {card.digital_wallet.email && (
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <Mail className="w-3 h-3" /> Wallet email: <span className="font-mono">{card.digital_wallet.email}</span>
                                </span>
                              )}
                              {card.digital_wallet.phone && (
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <Phone className="w-3 h-3" /> Wallet phone: <span className="font-mono">{card.digital_wallet.phone}</span>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-red-500 text-center py-2">Failed to load card details. Try again.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Physical Card Order Modal ── */}
          {physicalCardId && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-4" onClick={() => setPhysicalCardId(null)}>
              <div className="bg-card border border-border rounded-xl p-4 sm:p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto space-y-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Order Physical Card</h3>
                  <button onClick={() => setPhysicalCardId(null)} className="text-muted-foreground hover:text-foreground text-xl leading-none">✕</button>
                </div>
                <p className="text-sm text-muted-foreground">
                  A physical debit card will be embossed and shipped to the address below via Increase.
                </p>

                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cardholder Name (embossed on card)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">First Name</label>
                      <input value={physFirstName} onChange={e => setPhysFirstName(e.target.value)} placeholder="Alex" className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Last Name</label>
                      <input value={physLastName} onChange={e => setPhysLastName(e.target.value)} placeholder="Morgan" className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background" />
                    </div>
                  </div>

                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-2">Shipping Address</p>
                  <div>
                    <label className="block text-xs font-medium mb-1">Recipient Name (on label)</label>
                    <input value={physRecipient} onChange={e => setPhysRecipient(e.target.value)} placeholder="REDEX LLC" className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Address Line 1</label>
                    <input value={physLine1} onChange={e => setPhysLine1(e.target.value)} placeholder="7330 Rome Ln" className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Address Line 2 (optional)</label>
                    <input value={physLine2} onChange={e => setPhysLine2(e.target.value)} placeholder="Suite 200" className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs font-medium mb-1">City</label>
                      <input value={physCity} onChange={e => setPhysCity(e.target.value)} placeholder="Brooksville" className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">State</label>
                      <input value={physState} onChange={e => setPhysState(e.target.value.toUpperCase().slice(0,2))} placeholder="FL" maxLength={2} className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background uppercase" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">ZIP</label>
                      <input value={physZip} onChange={e => setPhysZip(e.target.value)} placeholder="34613" maxLength={10} className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium mb-1">Shipping Method</label>
                    <select value={physMethod} onChange={e => setPhysMethod(e.target.value as any)} className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background">
                      <option value="usps">USPS (standard)</option>
                      <option value="fedex_2_day">FedEx 2-Day</option>
                      <option value="fedex_priority_overnight">FedEx Priority Overnight</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <button
                    onClick={handleSubmitPhysicalCard}
                    disabled={physLoading}
                    className="flex-1 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {physLoading ? 'Placing Order…' : 'Place Order'}
                  </button>
                  <button
                    onClick={() => setPhysicalCardId(null)}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-muted"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ──────── TAB: Treasury Wallets ──────── */}
      {activeTab === 'wallets' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-lg font-semibold">Platform Treasury Wallets</h2>
            <button
              onClick={() => setShowCreateWallet(!showCreateWallet)}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add Wallet
            </button>
          </div>

          {showCreateWallet && (
            <div className="bg-card border border-border rounded-xl p-4 sm:p-5 space-y-4">
              <h3 className="font-semibold">Create Platform Wallet</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Purpose</label>
                  <select
                    value={newWalletPurpose}
                    onChange={(e) => setNewWalletPurpose(e.target.value)}
                    className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background"
                  >
                    <option value="treasury">Treasury</option>
                    <option value="operations">Operations</option>
                    <option value="escrow">Escrow</option>
                    <option value="payouts">Payouts</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Network</label>
                  <select
                    value={newWalletNetwork}
                    onChange={(e) => setNewWalletNetwork(e.target.value)}
                    className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background"
                  >
                    <option value="base">Base</option>
                    <option value="ethereum">Ethereum</option>
                    <option value="polygon">Polygon</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleCreateWallet}
                  disabled={creatingWallet}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {creatingWallet ? 'Creating…' : 'Create Wallet'}
                </button>
                <button
                  onClick={() => setShowCreateWallet(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {platformWallets.length === 0 && !showCreateWallet ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <Globe className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No treasury wallets configured.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {platformWallets.map((w) => (
                <div
                  key={w.id || w.address}
                  className="bg-card border border-border rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Globe className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="font-mono text-sm">{shortAddr(w.address)}</span>
                      <button
                        onClick={() => copyToClipboard(w.address)}
                        className="text-muted-foreground hover:text-foreground"
                        title="Copy full address"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                        {w.purpose}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 capitalize">
                        {w.network}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      <span className="break-all">ID: {w.id}</span>
                      {w.created_at && ` · Created ${new Date(w.created_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <a
                    href={
                      w.network === 'base'
                        ? `https://basescan.org/address/${w.address}`
                        : w.network === 'polygon'
                        ? `https://polygonscan.com/address/${w.address}`
                        : `https://etherscan.io/address/${w.address}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-1.5 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" /> Explorer
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ──────── TAB: Bank Linking (Plaid) ──────── */}
      {activeTab === 'banking' && (
        <div className="space-y-5 sm:space-y-6 min-w-0">
          <div>
            <h2 className="text-lg font-semibold">Bank Account Linking</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Link bank accounts via Plaid to enable ACH deposits and withdrawals.
            </p>
          </div>

          <div className="bg-card border border-border rounded-xl p-4 sm:p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Building2 className="w-5 h-5" /> Link a Bank Account
            </h3>
            <BankLinkButton
              onLinked={() => {
                toast.success('Bank account linked successfully');
                setBankRefreshKey((k) => k + 1);
              }}
              variant="primary"
            />
          </div>

          <div className="bg-card border border-border rounded-xl p-4 sm:p-5 min-w-0">
            <h3 className="font-semibold mb-3">Linked Accounts</h3>
            <BankAccounts refreshKey={bankRefreshKey} />
          </div>
        </div>
      )}

      {/* ──────── TAB: Wire / ACH Info ──────── */}
      {activeTab === 'wire' && (
        <div className="space-y-5 sm:space-y-6 min-w-0">
          {/* Header */}
          <div>
            <h2 className="text-lg font-semibold">Wire / ACH Instructions</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Share these details with investors or use for incoming wire deposits.
            </p>
          </div>

          {/* Entity Info */}
          {(wireInfo.entityName || wireInfo.phone || wireInfo.email) && (
            <div className="bg-card border border-border rounded-xl p-4 sm:p-5 space-y-2">
              {wireInfo.entityName && (
                <h3 className="font-bold text-lg">{wireInfo.entityName}</h3>
              )}
              {wireInfo.entityAddress && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4 flex-shrink-0" /> {wireInfo.entityAddress}
                </p>
              )}
              <div className="flex flex-wrap gap-4 text-sm">
                {wireInfo.phone && (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Phone className="w-4 h-4" /> {wireInfo.phone}
                  </span>
                )}
                {wireInfo.email && (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Mail className="w-4 h-4" /> {wireInfo.email}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Bank Information */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-4 sm:px-5 py-3 bg-muted/50 border-b border-border">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Bank Information
              </h3>
            </div>
            <div className="divide-y divide-border">
              {[
                { label: 'Receiving Bank', value: wireInfo.bankName },
                wireInfo.bankAddress ? { label: 'Bank Address', value: wireInfo.bankAddress } : null,
                { label: 'Account Name', value: wireInfo.accountName },
                wireInfo.accountType ? { label: 'Account Type', value: wireInfo.accountType } : null,
                { label: 'Account Number', value: wireInfo.accountNumber },
                { label: 'ABA / Routing Number', value: wireInfo.routingNumber },
                wireInfo.domesticWireRouting && wireInfo.domesticWireRouting !== wireInfo.routingNumber
                  ? { label: 'Domestic Wire Routing', value: wireInfo.domesticWireRouting }
                  : null,
                wireInfo.directDepositRouting && wireInfo.directDepositRouting !== wireInfo.routingNumber
                  ? { label: 'Direct Deposit / Electronic Payments', value: wireInfo.directDepositRouting }
                  : null,
                wireInfo.swiftCode ? { label: 'SWIFT Code', value: wireInfo.swiftCode } : null,
                wireInfo.internationalWireSwift && wireInfo.internationalWireSwift !== wireInfo.swiftCode
                  ? { label: 'International Wire SWIFT', value: wireInfo.internationalWireSwift }
                  : null,
              ]
                .filter(Boolean)
                .map((row: any) => (
                  <div
                    key={row.label}
                    className="flex items-start justify-between gap-3 px-4 sm:px-5 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">{row.label}</p>
                      <p className="font-medium mt-0.5 break-all">{row.value || '—'}</p>
                    </div>
                    {row.value && (
                      <button
                        onClick={() => copyToClipboard(row.value)}
                        className="text-muted-foreground hover:text-foreground shrink-0 mt-1"
                        title="Copy"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
            </div>
          </div>

          {/* Wire Memo */}
          {(wireInfo.memoTemplate || wireInfo.memoExample) && (
            <div className="bg-card border border-border rounded-xl p-4 sm:p-5 space-y-2">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Info className="w-4 h-4" /> Wire Memo / Reference
              </h3>
              {wireInfo.memoTemplate && (
                <p className="italic text-sm text-muted-foreground">
                  {wireInfo.memoTemplate}
                </p>
              )}
              {wireInfo.memoExample && (
                <p className="text-sm">
                  Example: "{wireInfo.memoExample}"
                </p>
              )}
            </div>
          )}

          {/* Confirm contact */}
          {wireInfo.confirmContact && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-yellow-800">BEFORE YOU SEND</p>
              <p className="text-sm text-yellow-700 mt-1">
                Always confirm details verbally with {wireInfo.confirmContact} before sending funds.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ──────── TAB: Smart Contracts ──────── */}
      {activeTab === 'contracts' && (
        <SmartContractsTab />
      )}

      {/* ──────── TAB: Share Conversion ──────── */}
      {activeTab === 'conversions' && (
        <ShareConversionAdminTab />
      )}

      {/* ──────── TAB: Liquidity & Distributions ──────── */}
      {activeTab === 'liquidity' && (
        <LiquidityDistributionsAdminTab />
      )}

      {/* ──────── TAB: Infrastructure ──────── */}
      {activeTab === 'clearing' && (
        <ProgramManagerDashboard />
      )}

      {activeTab === 'infra' && (
        <InfrastructureTab
          summary={financialSummary}
          railBreakdown={railBreakdown}
          statements={statements}
          pricing={pricing}
          onGenerateStatement={async () => {
            setGeneratingStatement(true);
            try {
              const now = new Date();
              await api.post('/admin/financials/statements/generate', {
                year: now.getFullYear(),
                month: now.getMonth() + 1,
              });
              toast.success('Statement generated');
              const res = await api.get('/admin/financials/statements') as any;
              if (res?.data) setStatements(Array.isArray(res.data) ? res.data : []);
            } catch (err: any) {
              toast.error(err?.message || 'Failed to generate statement');
            } finally {
              setGeneratingStatement(false);
            }
          }}
          generatingStatement={generatingStatement}
          fmtUSD={fmtUSD}
        />
      )}
    </div>
  );
};

/* ═══════════════════════ Infrastructure Sub-Component ═══════════════════════ */

interface InfraTabProps {
  summary: FinancialSummary | null;
  railBreakdown: RailBreakdown[];
  statements: TenantStatement[];
  pricing: TenantPricing | null;
  onGenerateStatement: () => void;
  generatingStatement: boolean;
  fmtUSD: (cents: number | string) => string;
}

const InfrastructureTab: React.FC<InfraTabProps> = ({
  summary, railBreakdown, statements, pricing,
  onGenerateStatement, generatingStatement, fmtUSD,
}) => {
  const s = summary || {
    transaction_count: 0, settlement_volume_cents: 0,
    total_processing_cost_cents: 0, total_platform_fee_cents: 0,
    total_infra_fee_cents: 0, total_float_cents: 0,
    net_revenue_cents: 0, avg_settlement_days: 0, monthly_saas_fee_cents: 0,
  };

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const now = new Date();

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Platform Infrastructure Economics</h2>
          <p className="text-sm text-muted-foreground">
            Revenue ledger for {monthNames[now.getMonth()]} {now.getFullYear()}
          </p>
        </div>
        <button
          onClick={onGenerateStatement}
          disabled={generatingStatement}
          className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <FileText className="w-4 h-4" />
          {generatingStatement ? 'Generating…' : 'Generate Statement'}
        </button>
      </div>

      {/* Revenue KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Settlement Volume</p>
          <p className="text-xl font-bold mt-1">{fmtUSD(s.settlement_volume_cents)}</p>
          <p className="text-xs text-muted-foreground mt-1">{s.transaction_count} transactions</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Processing Cost</p>
          <p className="text-xl font-bold mt-1 text-red-600">-{fmtUSD(s.total_processing_cost_cents)}</p>
          <p className="text-xs text-muted-foreground mt-1">Rail fees absorbed</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Float Revenue</p>
          <p className="text-xl font-bold mt-1 text-green-600">+{fmtUSD(s.total_float_cents)}</p>
          <p className="text-xs text-muted-foreground mt-1">~{s.avg_settlement_days || 0} days avg settlement</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Net Revenue</p>
          <p className={`text-xl font-bold mt-1 ${Number(s.net_revenue_cents) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {fmtUSD(s.net_revenue_cents)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">After all costs</p>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* By Rail */}
        <div className="bg-card border border-border rounded-xl p-4 sm:p-5 min-w-0">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4" /> Revenue by Rail
          </h3>
          {railBreakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground">No transactions this period.</p>
          ) : (
            <div className="space-y-3">
              {railBreakdown.map((r) => (
                <div key={r.rail_type} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <span className="text-sm font-medium uppercase">{r.rail_type}</span>
                    <span className="text-xs text-muted-foreground ml-2">{r.txn_count} txns</span>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium">{fmtUSD(r.volume_cents)}</p>
                    <p className={`text-xs ${Number(r.net_cents) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      Net: {fmtUSD(r.net_cents)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pricing Config */}
        {pricing && (
          <div className="bg-card border border-border rounded-xl p-4 sm:p-5 min-w-0">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4" /> Pricing Configuration
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Monthly SaaS Fee</span>
                <span className="font-medium">{fmtUSD(pricing.monthly_fee_cents)}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">ACH Cost</span>
                <span className="font-medium">{fmtUSD(pricing.ach_cost_cents)}/txn</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Wire Cost</span>
                <span className="font-medium">{fmtUSD(pricing.wire_cost_cents)}/txn</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Card Fee</span>
                <span className="font-medium text-right">{(pricing.card_cost_bps / 100).toFixed(2)}% + {fmtUSD(pricing.card_fixed_cents)}</span>
              </div>
              {pricing.investor_infra_fee_bps > 0 && (
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Investor Infra Fee</span>
                  <span className="font-medium">{(pricing.investor_infra_fee_bps / 100).toFixed(2)}%</span>
                </div>
              )}
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Fed Funds Rate</span>
                <span className="font-medium">{(pricing.fed_funds_rate_bps / 100).toFixed(2)}%</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">ACH Settlement</span>
                <span className="font-medium">{pricing.settlement_days_ach} days</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tenant sees: Infrastructure Statement */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 sm:px-5 py-3 bg-muted/50 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Monthly Infrastructure Statements
          </h3>
        </div>
        {statements.length === 0 ? (
          <div className="p-5 text-center text-sm text-muted-foreground">
            No statements generated yet. Click "Generate Statement" to create one for the current month.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="text-muted-foreground border-b border-border">
                  <th className="text-left p-3 font-medium">Period</th>
                  <th className="text-right p-3 font-medium">Volume</th>
                  <th className="text-right p-3 font-medium">Txns</th>
                  <th className="text-right p-3 font-medium">Processing</th>
                  <th className="text-right p-3 font-medium">Float</th>
                  <th className="text-right p-3 font-medium">SaaS Fee</th>
                  <th className="text-right p-3 font-medium">Net Revenue</th>
                  <th className="text-center p-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {statements.map((stmt) => {
                  const start = new Date(stmt.period_start);
                  return (
                    <tr key={stmt.id} className="border-b border-border hover:bg-muted/30">
                      <td className="p-3 font-medium">
                        {monthNames[start.getMonth()]} {start.getFullYear()}
                      </td>
                      <td className="p-3 text-right">{fmtUSD(stmt.settlement_volume_cents)}</td>
                      <td className="p-3 text-right">{stmt.total_transactions}</td>
                      <td className="p-3 text-right text-red-600">-{fmtUSD(stmt.total_processing_cost_cents)}</td>
                      <td className="p-3 text-right text-green-600">+{fmtUSD(stmt.total_float_revenue_cents)}</td>
                      <td className="p-3 text-right">{fmtUSD(stmt.monthly_saas_fee_cents)}</td>
                      <td className={`p-3 text-right font-medium ${Number(stmt.net_revenue_cents) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fmtUSD(stmt.net_revenue_cents)}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          stmt.status === 'finalized' ? 'bg-green-100 text-green-800' :
                          stmt.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {stmt.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* How Tenants See It */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-border rounded-xl p-4 sm:p-6 min-w-0">
        <h3 className="font-bold text-sm uppercase tracking-wider text-muted-foreground mb-4">
          What Tenants See: Infrastructure Statement
        </h3>
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 max-w-md min-w-0">
          <h4 className="font-bold">{TENANT.branding.name} Capital Infrastructure Statement</h4>
          <hr className="my-3" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Settlement Volume</span>
              <span className="font-medium">{fmtUSD(s.settlement_volume_cents)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Transactions</span>
              <span className="font-medium">{s.transaction_count}</span>
            </div>
            <hr className="my-2" />
            <p className="text-xs font-semibold text-gray-400 uppercase">Infrastructure Usage</p>
            <div className="flex justify-between"><span className="text-gray-500">ACH Rails</span><span className="text-green-600 font-medium">Included</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Compliance Engine</span><span className="text-green-600 font-medium">Included</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Risk Monitoring</span><span className="text-green-600 font-medium">Included</span></div>
            <hr className="my-2" />
            <div className="flex justify-between font-bold">
              <span>Monthly Infrastructure Fee</span>
              <span>{fmtUSD(s.monthly_saas_fee_cents || (pricing?.monthly_fee_cents || 0))}</span>
            </div>
          </div>
          <p className="mt-3 text-[10px] text-gray-400 italic">
            Transaction fees: $0.00 · Processing fees: $0.00
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminTreasury;
